import { ref, type Ref } from "vue";
import { storeToRefs } from "pinia";
import { useAccountStore } from "@/shared/stores/AccountStore";
import { useSelectedCustomerStore } from "@/shared/stores/SelectedCustomerStore";
import { ContractType } from "@/app/fbs-hub-app/shared/@types/ContractType";
import {
  type Meter,
  mapToMeterType,
} from "@/app/fbs-hub-app/shared/@types/Meter";
import {
  type GasContainer,
  mapToGasContainerType,
} from "@/app/fbs-hub-app/shared/@types/GasContainer";
import { getContainers } from "../services/getContainers.service";

export const useGetGasContainers = () => {
  const accountStore = useAccountStore();
  const { user } = storeToRefs(accountStore);
  const selectedCustomerStore = useSelectedCustomerStore();
  const { selectedCustomer } = storeToRefs(selectedCustomerStore);

  const gasContainers = ref<GasContainer[]>([]);
  const meters = ref<Meter[]>([]);
  const fetchingGasContainersError: Ref<string | undefined> = ref(undefined);
  const isFetchingGasContainers = ref(false);

  const onFetchingGasContainers = async () => {
    if (!user.value || !selectedCustomer.value) return;

    try {
      fetchingGasContainersError.value = undefined;
      isFetchingGasContainers.value = true;

      const containers = await getContainers({
        userId: user.value.userId,
        customerId: selectedCustomer.value.id,
      });

      const containersWithOrderContract = containers.filter(
        (container) => container.contract.type === ContractType.OrderContract,
      );

      const containersWithMeterContract = containers.filter(
        (container) => container.contract.type === ContractType.MeterContract,
      );

      meters.value =
        containersWithMeterContract.length > 0
          ? containersWithMeterContract.map((container) =>
              mapToMeterType(container),
            )
          : [];

      gasContainers.value = containersWithOrderContract.map(
        (container, index) => {
          // All customers (except Autogas customers) have either exactly one meter contract or none at all
          // So if there is exactly one meter contract, we can assume that it is linked to the gas container contract(s) of the same customer
          const meterContractId =
            containersWithMeterContract.length === 1
              ? containersWithMeterContract[0]!.contractId
              : undefined;
          return mapToGasContainerType(container, index, meterContractId);
        },
      );
    } catch (error) {
      fetchingGasContainersError.value = error as string;
    } finally {
      isFetchingGasContainers.value = false;
    }
  };
  return {
    gasContainers,
    meters,
    fetchingGasContainersError,
    isFetchingGasContainers,
    onFetchingGasContainers,
  };
};
